export default class VideoPreview {
  /**
   * Fetches the first few seconds of the video and returns a blob URL that points to the fetched segment.
   * @returns {Promise<string>}
   */
  static async generatePreview(videoUrl) {
    const bitrate = 2 * 1024 * 1024; // Assume 2 Mbps
    const bytesFor5s = (bitrate * 5) / 8;
    const startByte = 0;
    const endByte = bytesFor5s - 1;

    const request = new Request(videoUrl, {
      headers: { Range: `bytes=${startByte}-${endByte}` },
    });
    const response = await fetch(request);

    if (!response.ok) {
      throw new Error('Failed to fetch video segment');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /**
   * Captures a poster image at a fixed timestamp from the video URL.
   * @returns {Promise<String>} Data URL of the captured frame.
   */
  static async generatePoster(videoUrl) {
    const captureTime = 1;
    const video = document.createElement('video');
    Object.assign(video, {
      src: videoUrl,
      crossOrigin: 'anonymous',
      muted: true,
      playsInline: true,
    });

    await new Promise((res, rej) => {
      video.addEventListener('loadedmetadata', res, { once: true });
      video.addEventListener('error', rej, { once: true });
    });

    video.currentTime = Math.min(captureTime, video.duration);

    await new Promise((res, rej) => {
      video.addEventListener('seeked', res, { once: true });
      video.addEventListener('error', rej, { once: true });
    });

    const w = video.videoWidth;
    const h = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    video.remove();

    return canvas.toDataURL('image/png');
  }
}
